import { useEffect, useState, type JSX } from 'react';
import { currentState, turnOff, turnOn, type PushState } from '../lib/push.ts';

/**
 * The switch that lets this device ring.
 *
 * Per device, not per person: a teacher signs in from a laptop and a phone and decides separately
 * for each, because the phone is the one that matters at nine in the evening and the laptop is the
 * one that would rattle through a lesson.
 *
 * Nothing is asked on load. A permission prompt that appears by itself is the fastest way to be
 * refused for ever — browsers remember a refusal and there is no second chance to explain what it
 * was for — so the prompt only ever follows a deliberate click on this.
 */
export function PushToggle(): JSX.Element | null {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void currentState().then((found) => alive && setState(found));

    return () => {
      alive = false;
    };
  }, []);

  // A browser that cannot do this gets no control at all rather than one that does nothing.
  if (state === null || state === 'unsupported') return null;

  async function flip(): Promise<void> {
    setBusy(true);

    try {
      setState(state === 'on' ? await turnOff() : await turnOn());
    } catch {
      // A refusal, or a push service that will not talk to us: leave the switch as it was.
      setState(await currentState());
    } finally {
      setBusy(false);
    }
  }

  const blocked = state === 'blocked';
  const on = state === 'on';

  return (
    <button
      type="button"
      className={`pushbtn${on ? ' pushbtn--on' : ''}`}
      disabled={busy || blocked}
      onClick={() => void flip()}
      title={
        blocked
          ? 'Уведомления запрещены в настройках браузера для этого сайта'
          : on
            ? 'Уведомления на этом устройстве включены — нажмите, чтобы выключить'
            : 'Включить уведомления о сообщениях и сданных работах на этом устройстве'
      }
      aria-pressed={on}
      aria-label={on ? 'Выключить уведомления' : 'Включить уведомления'}
    >
      {/* Drawn rather than typed: an emoji is a different picture on every system. */}
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 3a6 6 0 0 0-6 6v3.5L4.5 15h15L18 12.5V9a6 6 0 0 0-6-6z" />
        <path d="M10 18a2 2 0 0 0 4 0" />
        {blocked || !on ? <path d="M4 4l16 16" /> : null}
      </svg>
    </button>
  );
}
